/**
 * 🔴 **HOW A PERSON SIGNS IN, AND HOW THEY CHANGE IT** (D123, PNK-138) — the shell's side.
 *
 * Devin: *"I also want an option for my users to change from a password to a SSO or a
 * passkey"*, and *"I have 2 email address … I want it so that if I signin with either
 * google SSO I can get in."*
 *
 * Those are two different operations and it is worth keeping them straight:
 *
 *   · **Signing in** with Google (`startGoogleSignIn`) needs no session and finds an
 *     account that already exists. It cannot create one — the server refuses.
 *   · **Connecting** a Google account (`startGoogleLink`) needs a live session. This is
 *     what makes a SECOND address work: once a Google account is attached, sign-in
 *     matches on the Google account id and the address on it stops mattering.
 *
 * Both hand the browser off to Google, so both end in a full-page navigation rather
 * than a fetch — an OAuth redirect cannot happen inside XHR.
 */
import { API_BASE_URL } from "@/api/base";
import { authFetch } from "./http";
import { IDENTITY_BASE } from "./identity";
import type { FetchLike } from "./http";

export const SOCIAL_SIGN_IN_PATH = `${IDENTITY_BASE}/sign-in/social`;
export const LINK_SOCIAL_PATH = `${IDENTITY_BASE}/link-social`;
export const METHODS_PATH = "/me/sign-in-methods";
/**
 * 🔴 The SIGN-IN screen's own question, and it must not be `/me/sign-in-methods`: that one
 * needs a session, and the page asking has none. Asking the wrong one 401s, which reads as
 * "Google is not configured" and silently hides the button on every deployment that has it.
 */
export const OPTIONS_PATH = "/auth/sign-in-options";
export const REMOVE_METHOD_PATH = "/me/sign-in-methods/remove";

export const GOOGLE = "google";

export interface SignInMethods {
  password: boolean;
  passkeys: number;
  /** Each connected account, named — `id` is what a removal addresses, never the provider. */
  social: { id: string; provider: string; email: string | null; picture: string | null }[];
  /** 🔴 The server's count. The screen disables its own remove buttons from this rather
   *  than counting for itself, so the rule and the affordance cannot drift apart. */
  total: number;
  googleAvailable: boolean;
}

const EMPTY: SignInMethods = { password: false, passkeys: 0, social: [], total: 0, googleAvailable: false };

/**
 * Ask Better Auth where to send the browser, then go there.
 *
 * 🔴 The server answers with a URL rather than redirecting, because this is a fetch:
 * following a 302 to Google inside XHR lands the OAuth page in a response body nobody
 * can see. We navigate the top-level document ourselves.
 */
async function beginOAuth(path: string, callbackURL: string, send: FetchLike): Promise<string | null> {
  try {
    const response = await send(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ provider: GOOGLE, callbackURL, errorCallbackURL: callbackURL }),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { url?: unknown; redirect?: unknown };
    return typeof body.url === "string" && body.url !== "" ? body.url : null;
  } catch {
    return null;
  }
}

/** Sign in with Google. No session required — this is how one is obtained. */
export async function startGoogleSignIn(fetchImpl?: FetchLike, callbackPath = "/"): Promise<boolean> {
  // Plain `fetch`, not `authFetch`: there is no session yet, and this is the request
  // that begins making one.
  const send: FetchLike = fetchImpl ?? ((input, init) => fetch(input, init));
  // `callbackPath` is where Google brings the browser back: the root, or the MCP sign-in page (D133).
  const url = await beginOAuth(SOCIAL_SIGN_IN_PATH, `${window.location.origin}${callbackPath}`, send);
  if (url === null) return false;
  window.location.assign(url);
  return true;
}

/**
 * Connect a Google account to the person already signed in.
 *
 * 🔴 This is the one that makes a SECOND address work, and it is deliberately only
 * reachable from a screen behind a session: the person proves they hold the Pinksy
 * account, then proves they hold the Google one. Attaching something you demonstrably
 * own to something else you demonstrably own.
 */
export async function startGoogleLink(fetchImpl?: FetchLike): Promise<boolean> {
  // `authFetch`, because this one is only legitimate with a session behind it.
  const send: FetchLike = fetchImpl ?? ((input, init) => authFetch(input, init));
  const url = await beginOAuth(LINK_SOCIAL_PATH, `${window.location.origin}/security`, send);
  if (url === null) return false;
  window.location.assign(url);
  return true;
}

/** What ways in this person has. Empty on failure — the screen says so rather than guessing. */
export async function fetchSignInMethods(fetchImpl?: FetchLike): Promise<SignInMethods | null> {
  try {
    const response = await authFetch(`${API_BASE_URL}${METHODS_PATH}`, { credentials: "include" }, fetchImpl);
    if (!response.ok) return null;
    return { ...EMPTY, ...((await response.json()) as Partial<SignInMethods>) };
  } catch {
    return null;
  }
}

export type RemoveOutcome = { ok: true; remaining: SignInMethods } | { ok: false; message: string };

/**
 * Remove one way in.
 *
 * 🔴 The server's sentence is passed through unchanged. The one that matters — *"this is
 * the only way you can sign in"* — is a rule the client must not paraphrase into
 * something softer, and must not pre-empt either: the count that decides is the one in
 * the database at the moment of the write, not the one this screen was drawn with.
 */
export async function removeSignInMethod(method: string, fetchImpl?: FetchLike): Promise<RemoveOutcome> {
  try {
    const response = await authFetch(
      `${API_BASE_URL}${REMOVE_METHOD_PATH}`,
      { method: "POST", headers: { "content-type": "application/json" }, credentials: "include", body: JSON.stringify({ method }) },
      fetchImpl,
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: unknown };
      return { ok: false, message: typeof body.message === "string" && body.message !== "" ? body.message : "that could not be removed" };
    }
    const body = (await response.json()) as { remaining?: Partial<SignInMethods> };
    return { ok: true, remaining: { ...EMPTY, ...(body.remaining ?? {}) } };
  } catch {
    return { ok: false, message: "could not reach the server" };
  }
}

/**
 * What the sign-in screen may offer, asked WITHOUT a session. Returns `false` on any
 * failure: a button that cannot be confirmed is better absent than broken.
 */
export async function fetchSignInOptions(fetchImpl?: FetchLike): Promise<{ google: boolean }> {
  const send: FetchLike = fetchImpl ?? ((input, init) => fetch(input, init));
  try {
    const response = await send(`${API_BASE_URL}${OPTIONS_PATH}`, { method: "GET" });
    if (!response.ok) return { google: false };
    const body = (await response.json()) as { google?: unknown };
    return { google: body.google === true };
  } catch {
    return { google: false };
  }
}

export const PHOTO_FROM_ACCOUNT_PATH = "/me/photos/from-account";

/**
 * Take the picture off a connected account into the person's own photo gallery (D123, PNK-143).
 *
 * 🔴 The BYTES are copied server-side into `pinksy-artifacts`, never linked: Google's picture
 * URLs rotate, and a linked one would tell Google every time anybody renders a call sheet.
 * Choosing which photograph is the face stays where it already lives — the profile screen.
 */
export async function photoFromAccount(accountId: string, fetchImpl?: FetchLike): Promise<{ ok: true } | { ok: false; message: string }> {
  try {
    const response = await authFetch(
      `${API_BASE_URL}${PHOTO_FROM_ACCOUNT_PATH}`,
      { method: "POST", headers: { "content-type": "application/json" }, credentials: "include", body: JSON.stringify({ accountId }) },
      fetchImpl,
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { message?: unknown };
      return { ok: false, message: typeof body.message === "string" && body.message !== "" ? body.message : "that picture could not be used" };
    }
    return { ok: true };
  } catch {
    return { ok: false, message: "could not reach the server" };
  }
}

export const SIGN_IN_PHOTO_PATH = "/me/sign-in-photo";

/**
 * The picture on the person's most-recently-used connected account (D124, PNK-144).
 *
 * 🔴 This is the SIGN-IN photograph, not the profile one. It follows the provider without
 * asking and is shown only in the person's own chrome; the face on a call sheet is a
 * separate, deliberate choice and is served by `GET /me/photo`.
 */
export async function fetchSignInPhoto(fetchImpl?: FetchLike): Promise<{ url: string | null; email: string | null }> {
  try {
    const response = await authFetch(`${API_BASE_URL}${SIGN_IN_PHOTO_PATH}`, { credentials: "include" }, fetchImpl);
    if (!response.ok) return { url: null, email: null };
    const body = (await response.json()) as { url?: unknown; email?: unknown };
    const text = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);
    return { url: text(body.url), email: text(body.email) };
  } catch {
    return { url: null, email: null };
  }
}
